import { useState, useCallback } from 'react';
import { pipeline, env } from '@huggingface/transformers';
import { toast } from 'sonner';

// Configure transformers.js
env.allowLocalModels = false;
env.useBrowserCache = false;

const MAX_IMAGE_DIMENSION = 1024;

interface BackgroundRemovalOptions {
  backgroundColor?: string;
  customBackground?: File | null;
  addShadow?: boolean;
  featherEdges?: number;
}

interface ProcessedImage {
  id: string;
  original: string;
  processed: string;
  blob: Blob;
}

function resizeImageIfNeeded(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, image: HTMLImageElement) {
  let width = image.naturalWidth;
  let height = image.naturalHeight;

  if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
    if (width > height) {
      height = Math.round((height * MAX_IMAGE_DIMENSION) / width);
      width = MAX_IMAGE_DIMENSION;
    } else {
      width = Math.round((width * MAX_IMAGE_DIMENSION) / height);
      height = MAX_IMAGE_DIMENSION;
    }

    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(image, 0, 0, width, height);
    return true;
  }

  canvas.width = width;
  canvas.height = height;
  ctx.drawImage(image, 0, 0);
  return false;
}

async function removeBackground(imageElement: HTMLImageElement): Promise<Blob> {
  const segmenter = await pipeline('image-segmentation', 'Xenova/segformer-b0-finetuned-ade-512-512', {
    device: 'webgpu',
  });
  
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  if (!ctx) throw new Error('Could not get canvas context');
  
  resizeImageIfNeeded(canvas, ctx, imageElement);
  const imageData = canvas.toDataURL('image/jpeg', 0.8);
  
  const result = await segmenter(imageData);
  
  if (!result || !Array.isArray(result) || result.length === 0 || !result[0].mask) {
    throw new Error('Invalid segmentation result');
  }
  
  const outputCanvas = document.createElement('canvas');
  outputCanvas.width = canvas.width;
  outputCanvas.height = canvas.height;
  const outputCtx = outputCanvas.getContext('2d');
  
  if (!outputCtx) throw new Error('Could not get output canvas context');
  
  outputCtx.drawImage(canvas, 0, 0);
  
  const outputImageData = outputCtx.getImageData(0, 0, outputCanvas.width, outputCanvas.height);
  const data = outputImageData.data;
  
  for (let i = 0; i < result[0].mask.data.length; i++) {
    const alpha = Math.round((1 - result[0].mask.data[i]) * 255);
    data[i * 4 + 3] = alpha;
  }
  
  outputCtx.putImageData(outputImageData, 0, 0);
  
  return new Promise((resolve, reject) => {
    outputCanvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to create blob'));
      },
      'image/png',
      1.0
    );
  });
}

async function applyBackgroundOptions(
  bgRemovedBlob: Blob,
  options: BackgroundRemovalOptions
): Promise<Blob> {
  const img = await loadImage(bgRemovedBlob);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  if (!ctx) throw new Error('Could not get canvas context');
  
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  
  // Apply background color or image
  if (options.backgroundColor) {
    ctx.fillStyle = options.backgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  } else if (options.customBackground) {
    const bgImg = await loadImage(options.customBackground);
    ctx.drawImage(bgImg, 0, 0, canvas.width, canvas.height);
  }
  
  // Add shadow if requested
  if (options.addShadow) {
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    ctx.shadowBlur = 20;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 10;
  }
  
  // Apply feathering by adjusting alpha
  if (options.featherEdges && options.featherEdges > 0) {
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    const tempCtx = tempCanvas.getContext('2d');
    if (tempCtx) {
      tempCtx.drawImage(img, 0, 0);
      tempCtx.filter = `blur(${options.featherEdges}px)`;
      ctx.drawImage(tempCanvas, 0, 0);
    }
  } else {
    ctx.drawImage(img, 0, 0);
  }
  
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to create blob'));
      },
      'image/png',
      1.0
    );
  });
}

function loadImage(file: Blob | File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve(img);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

export function useBackgroundRemoval() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [processedImages, setProcessedImages] = useState<ProcessedImage[]>([]);

  const processImage = useCallback(async (
    file: File,
    options: BackgroundRemovalOptions = {}
  ): Promise<ProcessedImage | null> => {
    try {
      const img = await loadImage(file);
      const bgRemovedBlob = await removeBackground(img);
      const finalBlob = await applyBackgroundOptions(bgRemovedBlob, options);
      
      const processed: ProcessedImage = {
        id: crypto.randomUUID(),
        original: URL.createObjectURL(file),
        processed: URL.createObjectURL(finalBlob),
        blob: finalBlob,
      };
      
      return processed;
    } catch (error) {
      console.error('Error processing image:', error);
      toast.error(`Failed to process image: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return null;
    }
  }, []);

  const processBatch = useCallback(async (
    files: File[],
    options: BackgroundRemovalOptions = {}
  ) => {
    setIsProcessing(true);
    setProgress(0);
    setProcessedImages([]);
    
    const results: ProcessedImage[] = [];
    
    for (let i = 0; i < files.length; i++) {
      toast.loading(`Processing image ${i + 1} of ${files.length}...`, { id: 'bg-removal-progress' });
      
      const result = await processImage(files[i], options);
      if (result) {
        results.push(result);
        setProcessedImages((prev) => [...prev, result]);
      }
      
      setProgress(((i + 1) / files.length) * 100);
    }
    
    toast.success(`Successfully processed ${results.length} of ${files.length} images`, { id: 'bg-removal-progress' });
    setIsProcessing(false);
    
    return results;
  }, [processImage]);

  const clearResults = useCallback(() => {
    processedImages.forEach(img => {
      URL.revokeObjectURL(img.original);
      URL.revokeObjectURL(img.processed);
    });
    setProcessedImages([]);
    setProgress(0);
  }, [processedImages]);

  return {
    isProcessing,
    progress,
    processedImages,
    processImage,
    processBatch,
    clearResults,
  };
}
